import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { TableTopPanel } from 'components/TableTopPanel';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { WorkOrderChangeSeasonData, WorkOrderChangeTotalData } from 'gql/graphql';
import { Loader } from 'uikit';

import type { AgGridReact } from 'ag-grid-react';
import { useMemo, useRef } from 'react';

import { uerWorkOrderChangeTotalsGql } from '../WorkOrderChangeQueries';

import { useWorkOrderChangeSummaryColumns } from './hooks';

import Style from './WorkOrderChangeSummaryTable.module.css';

interface IWorkOrderChangeSummaryTable {
    workOrderChangeId: number;
    costRounding: number;
    workOrderChangeSeasons: WorkOrderChangeSeasonData[];
}

export const WorkOrderChangeSummaryTable = (props: IWorkOrderChangeSummaryTable) => {
    const { workOrderChangeId, costRounding, workOrderChangeSeasons } = props;
    const variables = useMemo(() => ({ workOrderChangeId }), [workOrderChangeId]);

    const tableRef = useRef<AgGridReact | null>(null);
    const tableApi = useMemo(() => (tableRef && 'current' in tableRef ? tableRef.current : undefined), [tableRef]);

    const [workOrderResultData, workOrderResultStatus] = useRequest({
        requestId: 'uerWorkOrderChangeTotals',
        query: uerWorkOrderChangeTotalsGql,
        variables,
        withEffect: Boolean(workOrderChangeId),
    });

    const workOrderResult = useMemo(
        () => (workOrderResultData?.uerWorkOrderChangeTotals?.data || []) as WorkOrderChangeTotalData[],
        [workOrderResultData?.uerWorkOrderChangeTotals?.data],
    );
    const isLoading = workOrderResultStatus === RequestStatus.FETCHING;
    const { columns, isColumnVisible, fullSetSettingsColumns, tableSettingsConfirm } = useWorkOrderChangeSummaryColumns(
        {
            workOrderChangeId,
            costRounding,
            workOrderChangeSeasons,
        },
    );

    return (
        <>
            <TableTopPanel
                totalItems={workOrderResult?.length}
                fetchedItems={workOrderResult?.length}
                optionalColumns={fullSetSettingsColumns}
                isColumnVisible={isColumnVisible}
                handleTableSettingsConfirm={tableSettingsConfirm}
                tableApi={tableApi}
            />
            {isLoading && <Loader />}
            {!isLoading && (
                <div className={Style.workOrderChangeSummaryTable}>
                    <AgGridTable
                        ref={tableRef}
                        columnDefs={columns}
                        rowData={workOrderResult}
                        defaultColDef={{
                            sortable: false,
                        }}
                    />
                </div>
            )}
        </>
    );
};

WorkOrderChangeSummaryTable.displayName = 'WorkOrderChangeSummaryTable';
